import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Spin,
  Typography,
  message,
} from "antd";
import { HistoryOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import {
  formatSubscriptionAmount,
  formatSubscriptionDate,
} from "../subscriptionFormatters";

const { Text } = Typography;
const { TextArea } = Input;

type BillingFrequency = "monthly" | "quarterly" | "semiannual" | "annual";

interface CompanySummary {
  id: number;
  name: string;
}

interface RegularizationFormValues {
  period_start: Dayjs;
  period_count: number;
  billing_frequency: BillingFrequency;
  warehouse_count: number;
  warehouse_unit_price: number;
  revision_reason: string;
}

interface CompanyTermsResponse {
  company: {
    billing_frequency: BillingFrequency;
  };
  pricing: {
    monthly_quote: {
      billable_warehouse_count: number;
      warehouse_unit_price: number;
      currency: string;
    } | null;
  };
  settings: {
    default_warehouse_monthly_price: number;
    currency: string;
  };
}

interface Props {
  open: boolean;
  company: CompanySummary | null;
  onClose: () => void;
  onSuccess: () => void;
}

const frequencyMonths: Record<BillingFrequency, number> = {
  monthly: 1,
  quarterly: 3,
  semiannual: 6,
  annual: 12,
};

const addBillingMonths = (start: Dayjs, months: number) => {
  const shifted = start.add(months, "month");
  return start.date() === start.daysInMonth()
    ? shifted.endOf("month").startOf("day")
    : shifted;
};

const SubscriptionBillingRegularizationModal: React.FC<Props> = ({
  open,
  company,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm<RegularizationFormValues>();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState("XOF");
  const values = Form.useWatch([], form) as
    | Partial<RegularizationFormValues>
    | undefined;

  const loadDefaults = useCallback(async () => {
    if (!open || !company) return;
    setLoading(true);
    try {
      const response = await fetch(
        `/api/subscription/companies/${company.id}/terms`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
            "Content-Type": "application/json",
          },
        },
      );
      const payload = await response.json();
      if (!response.ok || !payload.success) {
        throw new Error(payload.message || "Chargement impossible");
      }
      const terms = payload.data as CompanyTermsResponse;
      const quote = terms.pricing.monthly_quote;
      setCurrency(quote?.currency || terms.settings.currency || "XOF");
      form.setFieldsValue({
        billing_frequency: terms.company.billing_frequency || "monthly",
        period_count: 1,
        warehouse_count: quote?.billable_warehouse_count || 1,
        warehouse_unit_price:
          quote?.warehouse_unit_price ||
          terms.settings.default_warehouse_monthly_price,
        revision_reason: "",
      });
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Chargement impossible",
      );
    } finally {
      setLoading(false);
    }
  }, [company, form, open]);

  useEffect(() => {
    if (open) loadDefaults();
    else form.resetFields();
  }, [form, loadDefaults, open]);

  const preview = useMemo(() => {
    const frequency = values?.billing_frequency || "monthly";
    const months = frequencyMonths[frequency];
    const periods = Number(values?.period_count || 0);
    const warehouses = Number(values?.warehouse_count || 0);
    const unitPrice = Number(values?.warehouse_unit_price || 0);
    const billedMonthsPerPeriod = frequency === "annual" ? 11 : months;
    const total = Math.max(
      0,
      periods * warehouses * unitPrice * billedMonthsPerPeriod,
    );
    const endDate =
      values?.period_start && periods > 0
        ? addBillingMonths(values.period_start, months * periods)
        : null;
    return { months, periods, billedMonthsPerPeriod, total, endDate };
  }, [values]);

  const submit = useCallback(
    async (formValues: RegularizationFormValues) => {
      if (!company) return;
      setSaving(true);
      try {
        const response = await fetch(
          `/api/subscription/companies/${company.id}/regularize-billing`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${localStorage.getItem("authToken")}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              ...formValues,
              period_start: formValues.period_start.format("YYYY-MM-DD"),
            }),
          },
        );
        const payload = await response.json();
        if (!response.ok || !payload.success) {
          throw new Error(payload.message || "Régularisation impossible");
        }
        const createdCount = payload.data?.created?.length || 0;
        const skippedCount = payload.data?.skipped?.length || 0;
        if (createdCount) {
          message.success(payload.message);
        } else {
          message.warning(payload.message);
        }
        if (skippedCount) {
          message.info(
            `${skippedCount} période(s) déjà facturée(s) ont été ignorée(s).`,
          );
        }
        onSuccess();
      } catch (error) {
        message.error(
          error instanceof Error ? error.message : "Régularisation impossible",
        );
      } finally {
        setSaving(false);
      }
    },
    [company, onSuccess],
  );

  return (
    <Modal
      centered
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Créer les factures impayées"
      cancelText="Annuler"
      confirmLoading={saving}
      width={860}
      title={
        <span>
          <HistoryOutlined style={{ marginRight: 8 }} />
          Régulariser la facturation — {company?.name || "Entreprise"}
        </span>
      }
    >
      <Spin spinning={loading}>
        <Alert
          showIcon
          type="warning"
          message="Constatation d'une dette historique"
          description="Cette opération crée des factures non réglées avec leurs échéances historiques. Elle ne renouvelle pas la licence et ne marque aucun paiement. Les périodes déjà facturées seront ignorées."
          style={{ marginBottom: 16 }}
        />

        <Form form={form} layout="vertical" onFinish={submit}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="period_start"
                label="Début de la première période non facturée"
                rules={[{ required: true, message: "Date requise" }]}
              >
                <DatePicker
                  format="DD/MM/YYYY"
                  style={{ width: "100%" }}
                  disabledDate={(date) => date && date.isAfter(dayjs(), "day")}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="billing_frequency"
                label="Périodicité historique"
                rules={[{ required: true }]}
              >
                <Select
                  options={[
                    { value: "monthly", label: "Mensuelle" },
                    { value: "quarterly", label: "Trimestrielle" },
                    { value: "semiannual", label: "Semestrielle" },
                    { value: "annual", label: "Annuelle" },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="period_count"
                label="Nombre de périodes à régulariser"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={120} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="warehouse_count"
                label="Warehouses facturables par période"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} max={10000} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="warehouse_unit_price"
                label="Tarif historique / Warehouse / mois"
                rules={[{ required: true }]}
              >
                <InputNumber min={1} precision={0} addonAfter={currency} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>

          <Descriptions bordered size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label="Durée totale">
              {preview.months * preview.periods} mois
            </Descriptions.Item>
            <Descriptions.Item label="Fin de période calculée">
              {preview.endDate
                ? formatSubscriptionDate(preview.endDate.format("YYYY-MM-DD"))
                : "À déterminer"}
            </Descriptions.Item>
            <Descriptions.Item label="Dette qui sera créée">
              <Text strong>
                {formatSubscriptionAmount(preview.total, currency)}
              </Text>
            </Descriptions.Item>
          </Descriptions>

          {values?.billing_frequency === "annual" && (
            <Alert
              type="info"
              showIcon
              message="Chaque période annuelle couvre 12 mois et en facture 11."
              style={{ marginTop: 16 }}
            />
          )}

          <Form.Item
            name="revision_reason"
            label="Motif de la régularisation"
            rules={[
              { required: true, whitespace: true, message: "Motif obligatoire" },
              { min: 3, max: 500, message: "Entre 3 et 500 caractères" },
            ]}
            style={{ marginTop: 16 }}
          >
            <TextArea
              rows={3}
              maxLength={500}
              showCount
              placeholder="Ex. Reconstitution des mensualités non facturées depuis janvier 2026"
            />
          </Form.Item>
        </Form>
      </Spin>
    </Modal>
  );
};

export default SubscriptionBillingRegularizationModal;
